import type { RefObject } from 'react';
import { Box, Button, Chip, Stack } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { FilterAltOff as ClearFiltersIcon, Search as SearchIcon } from '@mui/icons-material';
import { layoutGap } from '../../../theme/theme';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import { validationActionTooltips } from '../../../shared/config/actionHelpCatalog';
import { WorkspaceFilterSection } from '../../../shared/ui/workflow/WorkspaceFilterRail';
import WorkspaceSearchField from '../../../shared/ui/workflow/WorkspaceSearchField';

interface ValidationRailSearchSectionProps {
  searchQuery: string;
  onSearchChange: (query: string) => void;
  activeFilters: string[];
  onClearFilters: () => void;
  searchRef?: RefObject<HTMLDivElement | null>;
}

export default function ValidationRailSearchSection({
  searchQuery,
  onSearchChange,
  activeFilters,
  onClearFilters,
  searchRef,
}: ValidationRailSearchSectionProps) {
  const theme = useTheme();

  const searchSectionAction =
    activeFilters.length > 0 ? (
      <ActionHelpTooltip help={validationActionTooltips.resetFilters}>
        <Button
          size="small"
          startIcon={<ClearFiltersIcon sx={{ fontSize: 14 }} />}
          onClick={onClearFilters}
          sx={{
            minWidth: 0,
            px: 0.75,
            py: layoutGap.micro,
          }}
        >
          Reset all
        </Button>
      </ActionHelpTooltip>
    ) : undefined;

  return (
    <Box ref={searchRef} data-testid="validation-tour-target-rail-search">
      <WorkspaceFilterSection
        title="Search"
        description="Find issues by keyword"
        action={searchSectionAction}
      >
        <ActionHelpTooltip help={validationActionTooltips.searchQueue} placement="bottom-start">
          <WorkspaceSearchField
            value={searchQuery}
            onChange={onSearchChange}
            placeholder="Search issues..."
        ariaLabel="Search issues…"
            startIcon={<SearchIcon sx={{ fontSize: 16, color: theme.palette.text.secondary }} />}
          />
        </ActionHelpTooltip>
        {activeFilters.length > 0 && (
          <Stack
            direction="row"
            spacing={layoutGap.dense}
            alignItems="center"
            flexWrap="wrap"
            useFlexGap
            sx={{ mt: layoutGap.dense }}
          >
            {activeFilters.map((filter, index) => (
              <Chip
                key={index}
                label={filter}
                size="small"
                variant="outlined"
                sx={{
                  height: 20,
                  '& .MuiChip-label': { px: 0.75, py: 0 },
                }}
              />
            ))}
          </Stack>
        )}
      </WorkspaceFilterSection>
    </Box>
  );
}
